import { Injectable, Provider } from '@angular/core';
import { HttpClient, HttpRequest, HttpHeaders, HttpResponse, HttpEvent } from '@angular/common/http';
import * as _ from 'lodash';
import { BzAuthConstants } from '../bz-authentication/bz-auth-constant';
import { JwtHelper } from './jwt.helper';
import {catchError} from 'rxjs/operators';
import { Observable } from 'rxjs';



export interface IAuthConfig {
    globalHeaders: Array<Object>;
    headerName: string;
    headerPrefix: string;
    noJwtError: boolean;
    noTokenScheme?: boolean;
    tokenGetter: () => string | Promise<string>;
    tokenName: string;
}

export interface IAuthConfigOptional {
    headerName?: string;
    headerPrefix?: string;
    tokenName?: string;
    tokenGetter?: () => string | Promise<string>;
    noJwtError?: boolean;
    globalHeaders?: Array<Object>;
    noTokenScheme?: boolean;
}

export class AuthConfigConsts {
    public static DEFAULT_TOKEN_NAME = 'bizagi.auth.accesstoken';
    public static DEFAULT_HEADER_NAME = 'Authorization';
    public static HEADER_PREFIX_BEARER = 'Bearer ';
}

export const AuthConfigDefaults: IAuthConfig = {
    headerName: AuthConfigConsts.DEFAULT_HEADER_NAME,
    headerPrefix: null,
    tokenName: AuthConfigConsts.DEFAULT_TOKEN_NAME,
    tokenGetter: () => localStorage.getItem(AuthConfigDefaults.tokenName) as string,
    noJwtError: false,
    globalHeaders: [],
    noTokenScheme: false
};

/**
 * Allows for explicit authenticated HTTP requests.
 */

@Injectable()
export class RestApi {

    constructor(
        private http: HttpClient) {
        this.tokenStream = new Observable<string>((obs: any) => {
            obs.next(this.config.tokenGetter());
        });
        this.headers = new HttpHeaders();
        this.headers = this.headers.set('Content-Type', 'application/json');
    }

    private config: IAuthConfig;
    private refreshingToken = false;
    private buffer: Array<IBufferRequest> = new Array<IBufferRequest>();
    protected headers: HttpHeaders;

    public tokenStream: Observable<string>;

    public setAuthenticationToken(access_token: string) {
        this.headers = this.headers.set('Authorization', 'Bearer ' + access_token);
    }

    public setAuthorizationToken(access_token) {
        this.headers = new HttpHeaders();
        this.headers = this.headers.set('Content-Type', 'application/json');
        this.headers = this.headers.set('Authorization', access_token);
    }

    public getAuthenticationData(): IAuthenticationData {
        return JSON.parse(localStorage.getItem(BzAuthConstants.AUTH.COOKIES.AUTHDATA)) as IAuthenticationData;
    }

    public get(url: string): Observable<any> {
        return this.http.get(url, { headers: this.headers });
    }

    public post(url: string, body: any, options?: any): Observable<any> {
        return this.http.post(url, body, { ...options, headers: this.headers })
        .pipe(catchError(this._handleError));
    }

    public postFile(url: string, body: any) {
        const headers = _.cloneDeep(this.headers);
        return this.http.post(url, body, { headers: headers.delete('Content-Type') });
    }

    public put(url: string, body: any): Observable<any> {
        return this.http.put(url, body, { headers: this.headers });
    }

    public delete(url: string): Observable<any> {
        return this.http.delete(url, { headers: this.headers });
    }

    public deleteFromBody(url: string, body: any): Observable<any> {
        return this.http.request('delete', url, { body, headers: this.headers });
    }

    public patch(url: string, body: any): Observable<any> {
        return this.http.patch(url, body, { headers: this.headers });
    }

    public head(url: string): Observable<any> {
        return this.http.head(url, { headers: this.headers });
    }

    public requestCustomOptions(method: string, url: string, options: any) {
        return this.http.request(method, url, options);
    }

    public requestBlob(request: RestApiRequest, method: string): Observable<any> {
        let headers = _.cloneDeep(this.headers);
        if (!headers) {
            headers = new HttpHeaders();
        }
        headers = headers.set('content-type', 'application/json');
        headers = headers.set('responseType', 'Blob');

        if (!request.url) {
            throw new Error('URL is not set, please review your code');
        }

        if (!request.body && method === 'POST') {
            throw new Error('Body is not set, please review your code');
        }

        const url = this.resolveUrlFromParameters(request);

        switch (method) {
            case 'POST':
                return this.http.post(url, request.body, { headers, responseType: 'blob', observe: 'response' });

            case 'GET':
                return this.http.get(url, { headers, responseType: 'blob', observe: 'response' });

            default:
                throw new Error(('method not found'));
        }

    }

    public getFileNameFromHeaders(headers: any, defaultValue: string): string {
      if (headers && headers.hasOwnProperty('content-disposition')) {
            const splittedContent = headers.get('content-disposition').split('=');
            return splittedContent[1];
        }
        else {
            return defaultValue;
        }
    }

    private resolveUrlFromParameters(request: RestApiRequest | any): string {
        // Create url changing path and query string
        let url = request.url;
        if (request.parameters) {
            for (const k in request.parameters) {
                if (request.parameters.hasOwnProperty(k)) {
                    url = url.replace('{' + k + '}', request.parameters[k]);
                }
            }
        }
        return url;
    }

    private _handleError(error: any) {
      const errMsg = (error.message) ? error.message : error.status ? `${ error.status } - ${ error.statusText }` : 'Server error';
      return Observable.throw(error);
    }
}

class RestApiRequest {
    url: string;
    parameters: Array<any>;
    body: any;
}
